import React, { useEffect, useState } from 'react'
import { WorkedAtDto } from '../../../api/person/models/WorkedAtDto'
import { formatDuration } from '../../../utils/dateUtils'
import { getSortedWorkedAts, formatText } from '../../../utils/personUtils'
import ExpandableText from '../../common/expandable/ExpandableText'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'

interface GroupedJob {
    companyId: string
    companyName: string
    companyLogo: string
    roles: WorkedAtDto[]
}

const groupJobsByCompany = (jobs: WorkedAtDto[]): GroupedJob[] => {
    const sortedJobs = getSortedWorkedAts(jobs)

    const grouped: GroupedJob[] = []
    sortedJobs.forEach((job) => {
        const lastGroup = grouped[grouped.length - 1]
        if (lastGroup && lastGroup.companyId === job.companyId) {
            lastGroup.roles.push(job)
        } else {
            grouped.push({
                companyId: job.companyId,
                companyName: job.company?.name ?? 'Unknown Company',
                companyLogo: '/no-company-logo.png',
                roles: [job],
            })
        }
    })
    return grouped
}

const formatTotalDuration = (roles: WorkedAtDto[]) => {
    if (!roles.length) return ''
    const firstStartDate = roles[roles.length - 1].startDate
    const lastEndDate = roles[0].endDate || new Date().toISOString()
    const totalDuration = formatDuration(firstStartDate, lastEndDate)
    return totalDuration.duration
}

const CurrentBadge = () => (
    <span className="ml-2 px-2 py-0.5 rounded-full bg-ctxt-green/10 text-ctxt-green text-xs font-medium align-middle">
        Current
    </span>
)

export default function ExperienceTimeline({
    jobs,
}: {
    jobs: WorkedAtDto[]
}): React.JSX.Element {
    const [groupedJobs, setGroupedJobs] = useState<GroupedJob[]>([])

    useEffect(() => {
        setGroupedJobs(groupJobsByCompany([...jobs]))
    }, [jobs])

    return (
        <div className="timeline-container w-full">
            <div className="mt-1">
                {groupedJobs.map((group, index) =>
                    group.roles.length === 1 ? (
                        <div key={index}>
                            {/* Single Role Format */}
                            <div className="flex gap-4">
                                <div className="w-10 h-10 flex-shrink-0">
                                    <img
                                        src={group.companyLogo}
                                        alt={group.companyName}
                                        className="w-full h-full object-cover border rounded"
                                        onError={(e) => {
                                            e.currentTarget.onerror = null
                                            e.currentTarget.src =
                                                '/no-company-logo.png'
                                        }}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm sm:text-base">
                                        {group.roles[0].jobTitle}
                                        {!group.roles[0].endDate && (
                                            <Badge
                                                color={BadgeColor.Neutral}
                                                className="ml-2"
                                            >
                                                Current
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-sm text-gray-700">
                                        {group.companyName}
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-500">
                                        {
                                            formatDuration(
                                                group.roles[0].startDate,
                                                group.roles[0].endDate,
                                            ).fromTo
                                        }
                                        <span> · </span>
                                        {
                                            formatDuration(
                                                group.roles[0].startDate,
                                                group.roles[0].endDate,
                                            ).duration
                                        }
                                    </div>
                                    {group.roles[0].description && (
                                        <ExpandableText
                                            className="text-sm text-gray-700 mt-2"
                                            maxLines={3}
                                        >
                                            {formatText(
                                                group.roles[0].description ??
                                                    '',
                                            )}
                                        </ExpandableText>
                                    )}
                                </div>
                            </div>
                            {index !== groupedJobs.length - 1 && (
                                <div className="w-full border-b border-gray-200 my-4"></div>
                            )}
                        </div>
                    ) : (
                        <div key={index} className="company-group mb-4">
                            {/* Multiple Roles Format */}
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 flex-shrink-0 mr-3">
                                    <img
                                        src={group.companyLogo}
                                        alt={group.companyName}
                                        className="w-full h-full object-cover border rounded"
                                        onError={(e) => {
                                            e.currentTarget.onerror = null
                                            e.currentTarget.src =
                                                '/no-company-logo.png'
                                        }}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-sm sm:text-base break-words">
                                        <span className="text-[1.05rem] sm:text-[1.1rem] font-bold">
                                            {group.companyName}
                                        </span>
                                    </h3>
                                    <div className="text-xs sm:text-sm text-gray-500">
                                        {formatTotalDuration(group.roles)}
                                    </div>
                                </div>
                            </div>
                            {/* Role List */}
                            <div className="ml-[18px] sm:ml-[22px] border-l-2 border-gray-300 relative">
                                {group.roles.map((role, roleIndex) => {
                                    const overlapDuration = formatDuration(
                                        role.startDate,
                                        role.endDate,
                                    )
                                    return (
                                        <div
                                            key={roleIndex}
                                            className="relative mb-4 pl-5 sm:pl-6"
                                        >
                                            {/* Dot */}
                                            <div className="absolute left-[-5px] top-2 w-2 h-2 rounded-full bg-gray-300" />

                                            <div className="text-sm sm:text-base font-semibold">
                                                {role.jobTitle}
                                                {!role.endDate && (
                                                    <CurrentBadge />
                                                )}
                                            </div>
                                            <p className="text-xs sm:text-sm text-gray-500">
                                                {overlapDuration.fromTo}
                                                <span> · </span>
                                                {overlapDuration.duration}
                                            </p>

                                            {role.description && (
                                                <ExpandableText
                                                    className="text-sm text-gray-700"
                                                    maxLines={3}
                                                >
                                                    {formatText(
                                                        role.description ?? '',
                                                    )}
                                                </ExpandableText>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                            {index !== groupedJobs.length - 1 && (
                                <div className="w-full border-b border-gray-200 my-4"></div>
                            )}
                        </div>
                    ),
                )}
            </div>
        </div>
    )
}
